import L from "leaflet";
import { ParametroBoton } from "../../../../utils/fertilizacionUtils";

/** Botones de modo de coloreo del mapa de fertilización (panel superior): un
 *  botón por parámetro presente en el análisis, agrupado por color. Selección
 *  exclusiva (siempre un parámetro activo). La lista se rearma con
 *  `setParametros` al cambiar de análisis. */
export class FertilizacionColorModeControl {
  /** Contenedor del panel superior. */
  private _parent: HTMLElement | null = null;
  /** Notifica el parámetro elegido (clave canónica). */
  private _onModeChange: (mode: string) => void = () => {};
  /** Botones por parámetro (con su modo, etiquetas y handler, para rearmar/
   *  limpiar y alternar símbolo↔nombre según el activo). */
  private _paramBtns: {
    mode: string;
    simbolo: string;
    nombre: string;
    el: HTMLButtonElement;
    onClick: () => void;
  }[] = [];
  /** Parámetro actualmente activo. */
  private _activeMode = "";

  /**
   * Guarda el contenedor y el callback.
   * @param parent       Contenedor del panel superior.
   * @param onModeChange Notifica el parámetro elegido.
   */
  init(parent: HTMLElement, onModeChange: (mode: string) => void): void {
    this._parent = parent;
    this._onModeChange = onModeChange;
  }

  /** Rearma los botones por parámetro (fija el parámetro activo dado). */
  setParametros(parametros: ParametroBoton[], modoActivo: string): void {
    this._paramBtns.forEach((b) => {
      L.DomEvent.off(b.el, "click", b.onClick);
      b.el.remove();
    });
    this._paramBtns = [];
    this._activeMode = modoActivo;

    if (!this._parent) return;
    parametros.forEach((parametro) => {
      const btn = L.DomUtil.create("button", "sx-map-btn") as HTMLButtonElement;
      btn.textContent = parametro.simbolo;
      btn.title = parametro.nombre;
      btn.style.background = parametro.grupoColor;
      btn.style.color = "#fff";
      btn.style.border = "none";
      L.DomEvent.disableClickPropagation(btn);
      const onClick = (): void => this._select(parametro.key);
      L.DomEvent.on(btn, "click", onClick);
      this._parent!.appendChild(btn);
      this._paramBtns.push({
        mode: parametro.key,
        simbolo: parametro.simbolo,
        nombre: parametro.nombre,
        el: btn,
        onClick,
      });
    });

    this._applyActive();
  }

  /** Quita todos los listeners y referencias. */
  dispose(): void {
    this._paramBtns.forEach((b) => L.DomEvent.off(b.el, "click", b.onClick));
    this._paramBtns = [];
    this._parent = null;
  }

  /** Cambia el parámetro activo (si difiere), repinta el estado y notifica. */
  private _select(mode: string): void {
    if (this._activeMode === mode) return;
    this._activeMode = mode;
    this._applyActive();
    this._onModeChange(mode);
  }

  /** Refleja el parámetro activo: el activo muestra "símbolo (nombre)", con
   *  letra más grande + negrita; los inactivos, solo el símbolo. */
  private _applyActive(): void {
    this._paramBtns.forEach((b) => {
      const activo = b.mode === this._activeMode;
      b.el.textContent = activo ? `${b.simbolo} (${b.nombre})` : b.simbolo;
      b.el.style.fontSize = activo ? "var(--sx-text-base)" : "";
      b.el.style.fontWeight = activo ? "700" : "";
    });
  }
}
